import React, { forwardRef, useId, useState } from 'react';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

export interface PasswordInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: string;
  icon?: React.ReactNode;
  label?: string;
  className?: string;
}

export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ error, icon, label, className = '', id, ...props }, ref) => {
    const generatedId = useId();
    const resolvedId = id || `password-${generatedId}`;
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);

    return (
      <div className="relative">
        {label && (
          <label
            htmlFor={resolvedId}
            className="block text-sm font-medium text-primary-light-200 mb-1"
          >
            {label}
          </label>
        )}
        <div className="relative">
          {icon && (
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              {icon}
            </div>
          )}
          <input
            id={resolvedId}
            ref={ref}
            type={isPasswordVisible ? 'text' : 'password'}
            className={`
              w-full min-h-[40px] px-3 py-2 bg-primary-dark-100/65 border border-primary-dark-300/70 rounded-lg
              text-primary-light-100 placeholder-primary-light-400
              focus:outline-none focus:ring-2 focus:ring-ratuj-300/70 focus:border-transparent
              disabled:opacity-50 disabled:cursor-not-allowed
              ${icon ? 'pl-10' : ''}
              pr-11
              ${error ? 'border-status-error-500 focus:ring-status-error-500' : ''}
              ${className}
            `}
            {...props}
          />
          <button
            type="button"
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-primary-light-400 hover:text-primary-light-200 focus-outline rounded-r-lg"
            onClick={() => setIsPasswordVisible((currentValue) => !currentValue)}
            aria-label={isPasswordVisible ? 'Ukryj hasło' : 'Pokaż hasło'}
            aria-pressed={isPasswordVisible}
          >
            {isPasswordVisible ? (
              <EyeSlashIcon className="w-5 h-5" />
            ) : (
              <EyeIcon className="w-5 h-5" />
            )}
          </button>
        </div>
        {error && <p className="mt-1 text-sm text-status-error-500">{error}</p>}
      </div>
    );
  }
);

PasswordInput.displayName = 'PasswordInput';

export default PasswordInput;
